import { useEffect, useMemo, useState } from "react";
import { useWS } from "@/context/WSContext";
import { Sparkles, Star, Award, Trophy, Gem, Zap, Flame, Rocket, Diamond, Crown } from "lucide-react";

const BAND_ICONS = [Sparkles, Star, Award, Trophy, Gem, Zap, Flame, Rocket, Diamond, Crown];

function LevelUpBurst({ payload, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const tier = payload.tier || {};
  const accent = tier.accent || "#39FF14";
  const accent2 = tier.accent2 || "#00FFFF";
  const bandIdx = Math.min(9, Math.max(0, tier.band_index ?? 0));
  const Icon = BAND_ICONS[bandIdx] || Sparkles;
  const level = payload.to_level;
  const kindLabel = payload.kind === "buyer" ? "buyer" : "gifter";

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    const t = setTimeout(onDismiss, 5200);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [onDismiss]);

  // Confetti bits — larger, sharper glitch fragments than the gift takeover
  const bits = useMemo(
    () =>
      Array.from({ length: 32 }, (_, i) => ({
        key: i,
        angle: (i / 32) * 360 + Math.random() * 12,
        distance: 32 + Math.random() * 55,
        delay: Math.random() * 500,
        duration: 1600 + Math.random() * 1400,
        size: 6 + Math.random() * 14,
        color: Math.random() > 0.5 ? accent : accent2,
        char: ["◈", "◆", "▲", "✦", "▮", "▶"][Math.floor(Math.random() * 6)],
      })),
    [accent, accent2]
  );

  return (
    <div
      data-testid="levelup-takeover"
      onClick={onDismiss}
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center cursor-pointer transition-opacity duration-500 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
      style={{
        background: `radial-gradient(circle at center, ${accent}30 0%, #000 65%, #000 100%)`,
      }}
    >
      {/* Grid backdrop */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `linear-gradient(${accent2}22 1px, transparent 1px), linear-gradient(90deg, ${accent2}22 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
          animation: "gift-grid-drift 6s linear infinite",
        }}
      />

      {/* Rising rings */}
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="absolute rounded-full border-2 pointer-events-none"
          style={{
            width: 220,
            height: 220,
            borderColor: accent,
            animation: `gift-pulse-ring ${1.4 + i * 0.35}s ${i * 0.3}s ease-out infinite`,
            opacity: 0,
          }}
        />
      ))}

      {/* Hex badge (huge) */}
      <div
        className="relative z-10 h-48 w-48 md:h-56 md:w-56 flex items-center justify-center"
        style={{
          animation: visible ? "gift-icon-pop 1.1s cubic-bezier(0.34, 1.56, 0.64, 1)" : "none",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${accent} 0%, ${accent2} 50%, ${accent} 100%)`,
            clipPath: "polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)",
            boxShadow: `0 0 60px ${accent}, inset 0 0 30px rgba(0,0,0,0.5)`,
          }}
        />
        <div
          className="absolute inset-[9%] flex flex-col items-center justify-center gap-1"
          style={{
            background: "#0A0A0C",
            clipPath: "polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)",
          }}
        >
          <Icon
            className="h-16 w-16 md:h-20 md:w-20"
            strokeWidth={1.4}
            style={{ color: accent, filter: `drop-shadow(0 0 14px ${accent})` }}
          />
          <span
            className="font-display font-black leading-none text-4xl md:text-5xl"
            style={{ color: accent2, textShadow: `0 0 14px ${accent}` }}
          >
            {level}
          </span>
        </div>
      </div>

      {/* Bursting fragments */}
      {bits.map((b) => (
        <span
          key={b.key}
          className="absolute font-display font-black pointer-events-none"
          style={{
            fontSize: b.size,
            color: b.color,
            textShadow: `0 0 12px ${b.color}`,
            transform: `translate(${Math.cos((b.angle * Math.PI) / 180) * b.distance}vmin, ${Math.sin((b.angle * Math.PI) / 180) * b.distance}vmin)`,
            animation: `gift-burst ${b.duration}ms ${b.delay}ms cubic-bezier(0.15, 0.65, 0.55, 1) forwards`,
            opacity: 0,
          }}
        >
          {b.char}
        </span>
      ))}

      {/* Prompt */}
      <p
        className="relative z-10 mt-8 font-mono text-xs md:text-sm uppercase tracking-[0.5em]"
        style={{ color: accent2, textShadow: `0 0 12px ${accent2}` }}
      >
        // {kindLabel}.level.up
      </p>
      <h1
        data-testid="levelup-title"
        className="relative z-10 mt-2 font-display font-black text-5xl md:text-7xl glitch text-center px-4"
        data-text={`L${level} · ${tier.name || ""}`}
        style={{ color: accent }}
      >
        L{level} · {tier.name || "TIER"}
      </h1>
      <p
        className="relative z-10 mt-4 font-display font-bold text-white text-xl md:text-2xl"
        style={{
          animation: visible ? "gift-amount-slide 0.9s 0.4s cubic-bezier(0.16, 1, 0.3, 1) both" : "none",
        }}
      >
        ${((payload.lifetime_cents || 0) / 100).toFixed(2)} lifetime
      </p>
      <p
        className="relative z-10 mt-6 font-mono text-[10px] uppercase tracking-widest text-white/50"
        style={{
          animation: visible ? "gift-tipper-fade 0.8s 0.7s ease-out both" : "none",
        }}
      >
        // tap.anywhere.to.dismiss
      </p>
    </div>
  );
}

/**
 * Global level-up celebration overlay. Listens for `level_up` WebSocket
 * frames pushed by the backend when a lifetime_gifted/purchased increment
 * crosses a new level band, and also for a manual `neosync:levelup-preview`
 * CustomEvent (useful for testing + future replay UI).
 */
export default function LevelUpTakeover() {
  const { subscribe } = useWS();
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    const unsub = subscribe((payload) => {
      if (payload?.type !== "level_up") return;
      if (!payload.to_level) return;
      setQueue((q) => [...q, payload]);
    });
    return unsub;
  }, [subscribe]);

  useEffect(() => {
    const handler = (e) => {
      const p = e.detail;
      if (!p || !p.to_level) return;
      setQueue((q) => [...q, p]);
    };
    window.addEventListener("neosync:levelup-preview", handler);
    return () => window.removeEventListener("neosync:levelup-preview", handler);
  }, []);

  if (queue.length === 0) return null;
  const current = queue[0];
  const advance = () => setQueue((q) => q.slice(1));

  return (
    <LevelUpBurst
      key={`${current.kind}-${current.to_level}-${queue.length}`}
      payload={current}
      onDismiss={advance}
    />
  );
}
